// client/src/user/change-password/ChangePassword.js
import React, { useState, useContext } from "react";
import {
  Stack,
  TextField,
  Button,
  IconButton,
  InputAdornment,
  Typography,
} from "@mui/material";
import {
  Visibility,
  VisibilityOff,
  LockReset as LockResetIcon,
} from "@mui/icons-material";
import { GlobalContext } from "../../context/GlobalContext";
import { TranslateContext } from "../../context/TranslateContext";
import CommonDialog from "../../common/CommonDialog";
import { useHistory } from "react-router-dom";

const L = {
  changePassword: "Change Password",
  changePasswordSubtitle: "Update your account password",
  currentPassword: "Current Password",
  newPassword: "New Password",
  confirmPassword: "Confirm New Password",
  save: "Update Password",
  cancel: "Cancel",
  passwordMismatch: "Passwords do not match",
  minLength: "Minimum 6 characters",
};

const ChangePassword = ({ open, onClose, lang: propLang }) => {
  const { hitAxios } = useContext(GlobalContext);
  const { lang: ctxLang } = useContext(TranslateContext);
  const history = useHistory();
  const t = (k) => propLang?.[k] || ctxLang?.[k] || L[k] || k;

  const [form, setForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [show, setShow] = useState({
    current: false,
    new: false,
    confirm: false,
  });
  const [loading, setLoading] = useState(false);

  const handleClose = () => {
    setForm({ currentPassword: "", newPassword: "", confirmPassword: "" });
    setShow({ current: false, new: false, confirm: false });
    onClose();
  };

  const handleSubmit = async () => {
    if (form.newPassword !== form.confirmPassword) return;
    setLoading(true);
    const res = await hitAxios({
      path: "/api/user/change-password",
      post: true,
      obj: {
        currentPassword: form.currentPassword,
        newPassword: form.newPassword,
      },
    });
    setLoading(false);
    if (res?.data?.success) {
      handleClose();
      setTimeout(() => {
        localStorage.removeItem(process.env.REACT_APP_TOKEN + "_user");
        history.push("/user/login");
      }, 1000);
    }
  };

  const mismatch =
    form.confirmPassword.length > 0 &&
    form.newPassword !== form.confirmPassword;

  const disabled =
    !form.currentPassword ||
    !form.newPassword ||
    form.newPassword.length < 6 ||
    !form.confirmPassword ||
    mismatch ||
    loading;

  const EyeToggle = ({ field }) => (
    <InputAdornment position="end">
      <IconButton
        size="small"
        onClick={() => setShow((p) => ({ ...p, [field]: !p[field] }))}
        edge="end"
        sx={{ color: "text.disabled" }}
      >
        {show[field] ? (
          <VisibilityOff fontSize="small" />
        ) : (
          <Visibility fontSize="small" />
        )}
      </IconButton>
    </InputAdornment>
  );

  return (
    <CommonDialog
      open={open}
      onClose={handleClose}
      title={t("changePassword")}
      subtitle={t("changePasswordSubtitle")}
      icon={LockResetIcon}
      maxWidth="xs"
    >
      <Stack spacing={2.5} mt={1}>
        <TextField
          fullWidth
          autoFocus
          label={t("currentPassword")}
          type={show.current ? "text" : "password"}
          value={form.currentPassword}
          onChange={(e) =>
            setForm((p) => ({ ...p, currentPassword: e.target.value }))
          }
          InputProps={{ endAdornment: <EyeToggle field="current" /> }}
        />

        <TextField
          fullWidth
          label={t("newPassword")}
          type={show.new ? "text" : "password"}
          value={form.newPassword}
          onChange={(e) =>
            setForm((p) => ({ ...p, newPassword: e.target.value }))
          }
          helperText={
            form.newPassword.length > 0 && form.newPassword.length < 6
              ? t("minLength")
              : ""
          }
          error={form.newPassword.length > 0 && form.newPassword.length < 6}
          InputProps={{ endAdornment: <EyeToggle field="new" /> }}
        />

        <TextField
          fullWidth
          label={t("confirmPassword")}
          type={show.confirm ? "text" : "password"}
          value={form.confirmPassword}
          onChange={(e) =>
            setForm((p) => ({ ...p, confirmPassword: e.target.value }))
          }
          onKeyDown={(e) => e.key === "Enter" && !disabled && handleSubmit()}
          error={mismatch}
          helperText={mismatch ? t("passwordMismatch") : ""}
          InputProps={{ endAdornment: <EyeToggle field="confirm" /> }}
        />

        <Stack direction="row" spacing={1.5} justifyContent="flex-end">
          <Button
            variant="outlined"
            color="inherit"
            onClick={handleClose}
            sx={{ borderRadius: 2, textTransform: "none" }}
          >
            {t("cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={disabled}
            sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600 }}
          >
            {t("save")}
          </Button>
        </Stack>
      </Stack>
    </CommonDialog>
  );
};

export default ChangePassword;
